import { Component, ElementRef, computed, inject, input, output, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { I18nService } from '../../../core';

@Component({
  selector: 'app-search-input',
  standalone: true,
  imports: [FormsModule, LucideAngularModule],
  template: `
    <div class="search-shell" (click)="focusInput()">
      <lucide-icon name="search" class="search-icon"></lucide-icon>
      <input
        #searchInput
        type="search"
        [ngModel]="value()"
        (ngModelChange)="valueChange.emit($event)"
        [placeholder]="placeholderText()"
        class="search-input"
      />
    </div>
  `,
  styles: [
    `
      .search-shell {
        height: 36px;
        min-width: 220px;
        border: 1px solid var(--border);
        border-radius: 10px;
        background: var(--bg-surface);
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 0 10px;
        cursor: text;
        transition: border-color 140ms ease, box-shadow 140ms ease;
      }

      .search-shell:focus-within {
        border-color: color-mix(in srgb, var(--focus) 46%, var(--border));
        box-shadow: 0 0 0 2px color-mix(in srgb, var(--focus) 18%, transparent);
      }

      .search-icon {
        width: 16px;
        height: 16px;
        color: var(--muted);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        transform: translateY(-0.5px);
      }

      .search-input {
        flex: 1;
        min-width: 0;
        height: 100%;
        border: 0;
        outline: none;
        background: transparent;
        color: var(--text);
        font-size: 13px;
        line-height: 1.2;
        padding: 0;
        margin: 0;
      }

      .search-input::placeholder {
        color: var(--faint);
      }
    `,
  ],
})
export class SearchInputComponent {
  private readonly i18n = inject(I18nService);

  readonly value = input<string>('');
  readonly valueChange = output<string>();
  readonly placeholder = input<string>('');
  readonly placeholderText = computed(
    () => this.placeholder() || this.i18n.t('shared.search.placeholder')
  );

  private readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');

  focusInput(): void {
    this.searchInput()?.nativeElement.focus();
  }
}
